import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '../store/authStore'

interface WebSocketContextType {
  socket: Socket | null
  isConnected: boolean
  joinRoom: (room: string) => void
  leaveRoom: (room: string) => void
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined)

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const socketRef = useRef<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const { token } = useAuthStore()

  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
        setIsConnected(false)
      }
      return
    }

    const socketUrl = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001'
    
    socketRef.current = io(socketUrl, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    })

    socketRef.current.on('connect', () => {
      console.log('🔌 WebSocket connected')
      setIsConnected(true)
    })

    socketRef.current.on('disconnect', () => {
      console.log('🔌 WebSocket disconnected')
      setIsConnected(false)
    })

    socketRef.current.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error)
      setIsConnected(false)
    })

    return () => {
      socketRef.current?.disconnect()
    }
  }, [token])

  const joinRoom = (room: string) => {
    socketRef.current?.emit('join', room)
  }

  const leaveRoom = (room: string) => {
    socketRef.current?.emit('leave', room)
  }

  return (
    <WebSocketContext.Provider
      value={{
        socket: socketRef.current,
        isConnected,
        joinRoom,
        leaveRoom,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}

// Hook for real-time market prices
export function useLiveMarketPrices(marketIds: string[]) {
  const { socket, isConnected, joinRoom, leaveRoom } = useWebSocket()
  const [prices, setPrices] = useState<Record<string, { yes: number; no: number }>>({})

  useEffect(() => {
    if (!socket || !isConnected || marketIds.length === 0) return

    const room = `markets:${marketIds.join(',')}`
    joinRoom(room)

    const handlePriceUpdate = (data: { marketId: string; yes: number; no: number }) => {
      setPrices((prev) => ({
        ...prev,
        [data.marketId]: { yes: data.yes, no: data.no },
      }))
    }

    socket.on('price:update', handlePriceUpdate)

    return () => {
      leaveRoom(room)
      socket.off('price:update', handlePriceUpdate)
    }
  }, [socket, isConnected, marketIds, joinRoom, leaveRoom])

  return prices
}

// Hook for real-time portfolio updates
export function useLivePortfolio() {
  const { socket, isConnected } = useWebSocket()
  const [portfolio, setPortfolio] = useState<any>(null)

  useEffect(() => {
    if (!socket || !isConnected) return

    const handlePortfolioUpdate = (data: any) => {
      setPortfolio(data)
    }

    socket.on('portfolio:update', handlePortfolioUpdate)

    return () => {
      socket.off('portfolio:update', handlePortfolioUpdate)
    }
  }, [socket, isConnected])

  return portfolio
}

// Hook for real-time alerts
export function useLiveAlerts() {
  const { socket, isConnected } = useWebSocket()
  const [latestAlert, setLatestAlert] = useState<any>(null)

  useEffect(() => {
    if (!socket || !isConnected) return

    const handleNewAlert = (data: any) => {
      setLatestAlert(data)
    }

    socket.on('alert:new', handleNewAlert)

    return () => {
      socket.off('alert:new', handleNewAlert)
    }
  }, [socket, isConnected])

  return latestAlert
}

// Hook for real-time trade updates
export function useLiveTrades() {
  const { socket, isConnected } = useWebSocket()
  const [latestTrade, setLatestTrade] = useState<any>(null)

  useEffect(() => {
    if (!socket || !isConnected) return

    const handleTradeUpdate = (data: any) => {
      setLatestTrade(data)
    }

    socket.on('trade:update', handleTradeUpdate)

    return () => {
      socket.off('trade:update', handleTradeUpdate)
    }
  }, [socket, isConnected])

  return latestTrade
}

// Hook for bot execution updates
export function useLiveBotExecutions(botId?: string) {
  const { socket, isConnected, joinRoom, leaveRoom } = useWebSocket()
  const [execution, setExecution] = useState<any>(null)

  useEffect(() => {
    if (!socket || !isConnected) return

    const room = botId ? `bot:${botId}` : 'bots:all'
    joinRoom(room)

    const handleExecution = (data: any) => {
      setExecution(data)
    }

    socket.on('bot:execution', handleExecution)

    return () => {
      leaveRoom(room)
      socket.off('bot:execution', handleExecution)
    }
  }, [socket, isConnected, botId, joinRoom, leaveRoom])

  return execution
}
